import { consumeInterWorkerMessage } from './api.js'
import {
  LOCAL_CALL_TARGET,
  MESSAGE_TYPES,
  TIMING_CONSTANTS,
  TRANSPORT_MODES,
  type TransportMode,
  WS_CLOSE_CODES,
} from './constants.js'
import { getPublicKey } from './crypto.js'
import Executor from './executor.js'
import type { CallOptions, ClientOptions, FlatSandbox, WorkersChannelClient } from './types.js'
import { bytesToHex, flattenSandbox, log, sleep, to32Bytes } from './utils.js'

export const getWebSocketImplementation = async (): Promise<any> => {
  const g = global as any
  if (typeof g.WebSocket !== 'undefined') return g.WebSocket
  if (typeof g.MozWebSocket !== 'undefined') return g.MozWebSocket
  throw new Error(
    'WebSocket implementation not found. Requires Node 23+ global WebSocket or browser WebSocket.',
  )
}

export const newWorkersChannelClient = ({
  serviceUrl,
  websocketUrl,
  privateKey,
  sandbox = {},
  consumeInterval = TIMING_CONSTANTS.DEFAULT_CONSUME_INTERVAL,
  callTimeout = TIMING_CONSTANTS.DEFAULT_CALL_TIMEOUT,
  callTransport = TRANSPORT_MODES.AUTO,
  namespaceClient,
  forceConnect = false,
  onDuplicateRejected,
  onDisplaced,
}: ClientOptions): WorkersChannelClient => {
  const privKey = to32Bytes(privateKey)
  const publicKey = getPublicKey(privKey)

  const flatSandbox = flattenSandbox(sandbox)
  const hasSandboxMethods = Object.keys(flatSandbox).length > 0

  let needsWebSocket = false
  let effectiveTransport: TransportMode = callTransport

  switch (callTransport) {
    case TRANSPORT_MODES.HTTP:
      needsWebSocket = false
      effectiveTransport = TRANSPORT_MODES.HTTP
      break

    case TRANSPORT_MODES.WS:
      needsWebSocket = true
      effectiveTransport = TRANSPORT_MODES.WS
      if (!websocketUrl) {
        throw new Error(
          'Configuration error: callTransport="ws" requires websocketUrl to be provided.',
        )
      }
      break

    case TRANSPORT_MODES.AUTO:
      needsWebSocket = hasSandboxMethods && !!websocketUrl
      effectiveTransport = needsWebSocket ? TRANSPORT_MODES.WS : TRANSPORT_MODES.HTTP
      break

    default:
      throw new Error(
        `Invalid callTransport value: "${callTransport}". Must be one of: "http", "ws", or "auto".`,
      )
  }

  let wsConnected = false
  let closed = false
  let forceOnFirstConnect = forceConnect

  // Match master: pass the raw (possibly nested) sandbox to the executor.
  // The executor only does sandbox[method] lookup, so nested keys like
  // 'user.get' silently 404 — preserve that contract. flatSandbox is used
  // only above for the hasSandboxMethods count.
  const { call, callSandboxMethod, processInterWorkerMessage } = Executor({
    sandbox: sandbox as FlatSandbox,
    serviceUrl,
    privKey,
    publicKey,
    callTimeout,
    transport: effectiveTransport,
    wsConnected: () => wsConnected,
  })

  log(`starting worker ${bytesToHex(publicKey)}`)

  const sendJson = (conn: any, json: unknown, onError: (err: Error) => void): void => {
    try {
      conn.send(JSON.stringify(json))
    } catch (err: any) {
      onError(err)
    }
  }

  const startKeepAlive = (
    conn: any,
    onError: (err: Error) => void,
  ): ReturnType<typeof setInterval> => {
    const sendPing = (): void => {
      try {
        conn.send('')
      } catch (err: any) {
        onError(err)
      }
    }

    sendPing()

    return setInterval(sendPing, TIMING_CONSTANTS.WEBSOCKET_KEEPALIVE_INTERVAL)
  }

  const handleWebSocketMessage = async (data: string): Promise<void> => {
    try {
      const { message_type, worker_request } = JSON.parse(data)

      if (message_type === MESSAGE_TYPES.MESSAGE) {
        await processInterWorkerMessage(worker_request)
      }
    } catch (err) {
      log('error handling WebSocket message:', err)
    }
  }

  const connectWS = async (): Promise<void> => {
    log('connecting to WebSocket server:', websocketUrl)

    const WebSocket = await getWebSocketImplementation()

    return new Promise(resolve => {
      let conn: any = null
      let keepAliveTimer: ReturnType<typeof setInterval> | null = null
      let hasResolved = false

      const cleanup = (err?: Error): void => {
        if (hasResolved) return
        hasResolved = true

        wsConnected = false

        if (err) {
          log('WebSocket error:', err.message)
        }

        if (conn !== null) {
          try {
            conn.close()
          } catch (_e) {
            // Ignore close errors
          }
        }

        if (keepAliveTimer !== null) {
          clearInterval(keepAliveTimer)
        }

        resolve()
      }

      try {
        conn = new WebSocket(websocketUrl)

        conn.onopen = () => {
          log('WebSocket connected')
          wsConnected = true

          sendJson(
            conn,
            {
              public_key: bytesToHex(publicKey),
              ...(forceOnFirstConnect && { force: true }),
            },
            cleanup,
          )
          forceOnFirstConnect = false

          keepAliveTimer = startKeepAlive(conn, cleanup)
        }

        conn.onmessage = async ({ data }: { data: string }) => {
          await handleWebSocketMessage(data)
        }

        conn.onerror = (e: any) => {
          cleanup(new Error(`WebSocket error: ${e.message || 'unknown'}`))
        }

        conn.onclose = (e: any) => {
          if (e.code === WS_CLOSE_CODES.DUPLICATE_REJECTED) {
            closed = true
            log(
              'FATAL: Connection rejected — another instance with the same key is already online.',
            )
            if (onDuplicateRejected) onDuplicateRejected(e)
          } else if (e.code === WS_CLOSE_CODES.DISPLACED) {
            closed = true
            log('FATAL: Displaced by a new instance with force flag.')
            if (onDisplaced) onDisplaced(e)
          }
          cleanup(new Error(`WebSocket closed [code=${e.code}] ${e.reason || 'no reason'}`))
        }
      } catch (err: any) {
        cleanup(err)
      }
    })
  }

  const consumeWSMessages = async (): Promise<void> => {
    while (!closed) {
      await connectWS()
      if (closed) break

      log('WebSocket disconnected, reconnecting...')
      await sleep(TIMING_CONSTANTS.WEBSOCKET_RECONNECT_DELAY)
    }
  }

  const keepConsumeInterWorkerMessage = async (): Promise<void> => {
    while (!closed) {
      try {
        const { requests } = await consumeInterWorkerMessage({
          publicKey,
          privKey,
          serviceUrl,
          payload: {
            message_type: MESSAGE_TYPES.REQUEST,
          },
        })

        if (requests && requests.length > 0) {
          log(`received ${requests.length} offline messages`)

          for (const request of requests) {
            try {
              await processInterWorkerMessage(request)
            } catch (err) {
              log('error processing offline message:', err)
            }
          }
        }
      } catch (err) {
        log('error polling for messages:', err)
      }

      await sleep(consumeInterval)
    }
  }

  if (needsWebSocket) {
    const reasons: string[] = []
    if (hasSandboxMethods) {
      reasons.push('sandbox has methods')
    }
    reasons.push(`effectiveTransport=${effectiveTransport}`)
    log(`starting WebSocket connection and message polling (${reasons.join(', ')})`)
    consumeWSMessages()
    keepConsumeInterWorkerMessage()
  } else {
    log(`pure HTTP mode (callTransport=${callTransport}, no sandbox, no WebSocket)`)
  }

  const callByName = async (
    workerName: string,
    method: string,
    params?: unknown[],
    opts?: CallOptions,
  ): Promise<unknown> => {
    if (workerName === LOCAL_CALL_TARGET) {
      return call(LOCAL_CALL_TARGET, method, params, opts)
    }

    if (!namespaceClient) {
      throw new Error(
        'callByName requires namespaceClient to be provided in client options. ' +
          'Pass a namespaceClient with getPublicKey(name) method when creating the client.',
      )
    }

    const resolvedKey = await namespaceClient.getPublicKey(workerName)

    try {
      return await call(resolvedKey, method, params, opts)
    } catch (err: any) {
      err.workerName = workerName
      err.message = `[${workerName}] ${err.message}`
      throw err
    }
  }

  return {
    call,
    callByName,
    callSandboxMethod,
    publicKey,
    close() {
      closed = true
    },
  }
}
